import { ColumnSeries, DateAxis, Legend, LineSeries, ValueAxis, XYChart, XYCursor } from '@amcharts/amcharts4/charts';
import { BindingEngine } from 'aurelia-binding';
import { widget } from '../../widgets/widget-decorator';
import { color, create, percent, unuseTheme, useTheme } from '@amcharts/amcharts4/core';
import { ChartWidgetBase } from '../chart-widget-base';
import { CustomerService } from '../../services/customer-service';
import { CustomerTotalValuesReportViewModel } from '../../clients/reporting-clients';
import { IApplicationFilter } from '../../interfaces/i-application-filter';
import { startOfYear } from 'date-fns/startOfYear';
import { formatISO } from 'date-fns/formatISO';
import { customElement, useView } from 'aurelia-templating';
import { PLATFORM } from 'aurelia-framework';
import { getChartReadyPromise } from '../../services/chart-ready';
import { PortfolioReportCache } from '../../services/portfolio-report-cache';
import { property } from '../property-decorator';
import { WidgetPropertyType } from '../widget-property-type';
import { enumToExtendedArray } from '../../services/enum-to-extended-array';

export enum PortfolioValueChartType {
  Line = 1,
  Bar = 2
}

export enum PortfolioValueWidgetStartFrom {
  ToDateYearStart = 1,
  FromDate = 2,
  FromDateYearStart = 3,
  FirstTransaction = 4
}

declare module '../../clients/reporting-clients' {
  interface CustomerTotalValuesReportViewModel {
    pl: number;
  }
}

@customElement('portfolio-value-widget')
@useView(PLATFORM.moduleName('./portfolio-value-widget.html'))
@widget('Portfolio value chart', true, true)
export class PortfolioValueWidget extends ChartWidgetBase {
  constructor(element: Element, customerService: CustomerService, bindingEngine: BindingEngine, private portfolioReportCache: PortfolioReportCache) {
    super(element, bindingEngine, customerService);
  }

  chartObject: XYChart;
  totals: CustomerTotalValuesReportViewModel[];
  showValue = true;
  showPL = false;
  noGL: boolean;

  @property('Data', 'Type', WidgetPropertyType.List, enumToExtendedArray(PortfolioValueChartType))
  chartType: PortfolioValueChartType = PortfolioValueChartType.Line;

  @property('Data', undefined, WidgetPropertyType.List, [
    { value: PortfolioValueWidgetStartFrom.ToDateYearStart, label: '"To" date year start' },
    { value: PortfolioValueWidgetStartFrom.FromDate, label: '"From" date' },
    { value: PortfolioValueWidgetStartFrom.FromDateYearStart, label: '"From" date year start' },
    { value: PortfolioValueWidgetStartFrom.FirstTransaction, label: 'First transaction' }
  ])
  startFrom: PortfolioValueWidgetStartFrom = PortfolioValueWidgetStartFrom.ToDateYearStart;

  @property('Data', 'Hist. months')
  historicalMonths?: number = 60;

  @property('Style')
  showMode: boolean = true;

  @property('Style')
  chartTheme?: string;

  @property('Style', 'Label color')
  labelColour?: string;

  @property('Style')
  legend?: boolean;

  override async loadData(filter: IApplicationFilter) {
    let fromDate: Date | undefined;
    switch (this.startFrom) {
      case PortfolioValueWidgetStartFrom.ToDateYearStart: fromDate = startOfYear(filter.dates.to); break;
      case PortfolioValueWidgetStartFrom.FromDate: fromDate = filter.dates.from; break;
      case PortfolioValueWidgetStartFrom.FromDateYearStart: fromDate = startOfYear(filter.dates.from); break;
      case PortfolioValueWidgetStartFrom.FirstTransaction: fromDate = new Date(2000, 0, 1); break;
    }
    const fromDateStr = formatISO(fromDate, { representation: 'date' });
    return this.portfolioReportCache.customerTotals(filter.customer.companyId, fromDateStr, filter.dates.toStr, undefined, false, this.historicalMonths);
  }

  override async setData(data: CustomerTotalValuesReportViewModel[]) {
    this.totals = data;
    this.totals.forEach(x => x.pl = x.estimatedValue + x.costPriceClosingBalance);
    this.noGL = !data[data.length - 1]?.netAssetValueClosingBalance;
    switch (this.chartType) {
      case PortfolioValueChartType.Line:
        await this.createLineChart();
        break;
      case PortfolioValueChartType.Bar:
        await this.createColumnChart();
        break;
    }
  }

  async createLineChart() {
    const theme = this.customerService.getChartTheme(this.chartTheme);
    useTheme(theme);
    this.chartObject?.dispose();
    this.chartObject = create(this.chart, XYChart);
    const readyPromise = getChartReadyPromise(this.chartObject);
    this.chartObject.height = percent(100);
    this.chartObject.hiddenState.properties.opacity = 0;
    this.chartObject.fontSize = 9;
    const labelColour = color(this.getChartLabelColour(this.labelColour));
    if (this.legend) {
      this.chartObject.legend = new Legend();
      this.chartObject.legend.labels.template.stroke = labelColour;
    }

    const xAxis = this.chartObject.xAxes.push(new DateAxis());
    xAxis.dataFields.date = 'date';
    xAxis.renderer.labels.template.wrap = true;
    xAxis.renderer.labels.template.rotation = -90;
    xAxis.renderer.minGridDistance = 30;
    xAxis.renderer.grid.template.location = 0.5;
    xAxis.renderer.labels.template.horizontalCenter = 'right';
    xAxis.renderer.labels.template.verticalCenter = 'middle';
    xAxis.renderer.labels.template.fill = labelColour;
    xAxis.renderer.ticks.template.stroke = labelColour;

    const yAxis = this.chartObject.yAxes.push(new ValueAxis());
    yAxis.numberFormatter.numberFormat = '#a';
    yAxis.tooltip.align = 'right';
    yAxis.tooltip.contentAlign = 'right';
    yAxis.tooltip.numberFormatter.numberFormat = '#,###.00a';
    yAxis.updateTooltip = () => { };
    yAxis.tooltip.pointerOrientation = 'left';
    yAxis.renderer.labels.template.fill = labelColour;
    yAxis.renderer.ticks.template.stroke = labelColour;

    const series = this.chartObject.series.push(new LineSeries());
    series.name = this.showValue ? 'Portfolio value' : 'P&L';
    series.stacked = false;
    series.dataFields.dateX = 'dateClosingBalance';
    series.dataFields.valueY = this.showValue ? 'estimatedValue' : 'pl';
    series.fillOpacity = 0.5;
    series.tooltipText = '[bold]{name}[/] {dateX}: {valueY.formatNumber("#,###.00a")}';
    series.strokeWidth = 2;
    series.strokeOpacity = 1;
    series.fillOpacity = 0;

    this.chartObject.cursor = new XYCursor();
    unuseTheme(theme);
    this.chartObject.data = this.totals;
    await readyPromise;
  }

  async createColumnChart() {
    const theme = this.customerService.getChartTheme(this.chartTheme);
    useTheme(theme);
    this.chartObject?.dispose();
    this.chartObject = create(this.chart, XYChart);
    const readyPromise = getChartReadyPromise(this.chartObject);
    this.chartObject.height = percent(100);
    this.chartObject.hiddenState.properties.opacity = 0;
    this.chartObject.fontSize = 9;
    const labelColour = color(this.getChartLabelColour(this.labelColour));

    const dateAxis = this.chartObject.xAxes.push(new DateAxis());
    dateAxis.baseInterval = { timeUnit: 'day', count: 1 };
    dateAxis.groupCount = 20;
    dateAxis.groupData = true;
    dateAxis.zoomable = false;
    dateAxis.renderer.labels.template.wrap = true;
    dateAxis.renderer.labels.template.rotation = -90;
    dateAxis.renderer.labels.template.fill = labelColour;
    dateAxis.renderer.minGridDistance = 30;
    dateAxis.renderer.grid.template.location = 0.5;
    dateAxis.renderer.labels.template.horizontalCenter = 'right';
    dateAxis.renderer.labels.template.verticalCenter = 'middle';

    const valueAxis = this.chartObject.yAxes.push(new ValueAxis());
    valueAxis.numberFormatter.numberFormat = '#a';
    valueAxis.renderer.labels.template.fill = labelColour;
    valueAxis.zoomable = false;
    valueAxis.min = 0;

    const colSeries = this.chartObject.series.push(new ColumnSeries());
    colSeries.xAxis = dateAxis;
    colSeries.yAxis = valueAxis;
    colSeries.dataFields.dateX = 'date';
    colSeries.dataFields.valueY = this.showValue ? 'estimatedValue' : 'pl';;

    colSeries.numberFormatter.numberFormat = '#,###.';
    colSeries.columns.template.tooltipText = '[bold]{name}[/] {dateX}: {valueY}';
    this.chartObject.responsive.enabled = true;
    unuseTheme(theme);
    this.chartObject.data = this.totals;
    await readyPromise;
  }

  override configChanged(key: keyof PortfolioValueWidget) {
    switch (key) {
      case 'labelColour':
        const labelColour = color(this.getChartLabelColour(this.labelColour));
        this.chartObject.xAxes.each(x => x.renderer.labels.template.fill = x.renderer.ticks.template.stroke = labelColour);
        this.chartObject.yAxes.each(x => x.renderer.labels.template.fill = x.renderer.ticks.template.stroke = labelColour);
        this.refresh$.next();
        break;
      default:
        this.refresh$.next();
        break;
    }
  }

  modeChanged() {
    this.refresh$.next();
  }
}
